import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Discover, Wishlist, type VmpDiscoverProduct } from '@/lib/api/v1';
import { Button, Card, EmptyState, Skeleton } from '@/design-system';
import { CatalogProductCard, catalogProductImageUrl } from '@/components/CatalogProductCard';
import { useToasts } from '@/lib/store';

/**
 * Nyheter — what just hit the primary product catalog.
 *
 * Powered by the public consumer API (vmpws/v2) via our backend proxy
 * (`/api/v1/catalog/nyheter`). Returns products flagged with the
 * `newProducts:true` facet — the catalog's own "ny i utvalget" signal.
 *
 * The category filter maps 1:1 to the catalog's `mainCategory` codes so
 * users browsing Alkisen can filter Nyheter by wine vs. beer vs. sprit.
 */

interface CategoryOption {
  code: string;
  label: string;
  /** Lucide icon code already in our icon map. */
  icon: 'bottle' | 'beer' | 'spirits' | 'sparkles' | 'grape';
}

const CATEGORIES: CategoryOption[] = [
  { code: '', label: 'Alle', icon: 'sparkles' },
  { code: 'rødvin', label: 'Rødvin', icon: 'bottle' },
  { code: 'hvitvin', label: 'Hvitvin', icon: 'bottle' },
  { code: 'rosévin', label: 'Rosé', icon: 'bottle' },
  { code: 'musserende_vin', label: 'Musserende', icon: 'bottle' },
  { code: 'brennevin', label: 'Brennevin', icon: 'spirits' },
  { code: 'øl', label: 'Øl', icon: 'beer' },
  { code: 'sider', label: 'Sider', icon: 'grape' },
];

export function NyheterPage() {
  const [category, setCategory] = useState<string>('');

  const nyheterQ = useQuery({
    queryKey: ['nyheter', category],
    queryFn: () => Discover.nyheter({ category: category || undefined, pageSize: 36 }),
    staleTime: 30 * 60_000,
  });

  const products = nyheterQ.data?.products ?? [];
  const total = nyheterQ.data?.total ?? 0;

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Nyheter</h1>
        <p className="text-sm text-mist">
          Akkurat lansert i katalogen. {total > 0 && <span className="text-candle">{total} produkter</span>}
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.code}
            type="button"
            onClick={() => setCategory(c.code)}
            className={`ui-pill ${category === c.code ? 'ui-pill--active' : ''}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {nyheterQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-4">
              <Skeleton height={176} rounded="md" />
              <Skeleton height={18} width="80%" />
              <Skeleton height={12} width="60%" />
            </Card>
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon="sparkles"
          title="Ingen nyheter"
          description="Ingen nye lanseringer i denne kategorien akkurat nå."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => (
            <DiscoverCard key={p.code} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function DiscoverCard({ product }: { product: VmpDiscoverProduct }) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const image = catalogProductImageUrl(product);
  const country = product.main_country?.name;
  const region = product.district?.name;

  const addWishlist = useMutation({
    mutationFn: () =>
      Wishlist.add({
        productId: product.code,
        name: product.name,
        region,
        country,
        imageUrl: image,
        priceNok: product.price?.value,
      }),
    onSuccess: () => {
      push({ title: 'Lagt på ønskelisten', description: product.name, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['wishlist'] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke legge til', description: err.message, tone: 'danger' }),
  });

  return (
    <CatalogProductCard
      product={product}
      action={
        <Button
          size="sm"
          variant="ghost"
          iconLeft="heart"
          loading={addWishlist.isPending}
          fullWidth
          onClick={() => addWishlist.mutate()}
        >
          Ønskeliste
        </Button>
      }
    />
  );
}
